import {HttpClient} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {map, Observable, tap} from 'rxjs';
import {AuthRequest, Login, UserRegistration} from "../interfaces/auth-request";
import {AuthoritiesConstants, UPermission} from "../enums/UPermission";
import {UserImpl} from "../../shared/services/user-impl.service";
import {UserResourceService} from "./user-resource.service";

interface decodeUser {
  auth: string,
  auth_json: string,
  exp: number,
  name: string,
  sub: string
}

/**
 * Сервис для аутентификации и регистрации пользователя.
 *
 * @example
 * ```ts
 * @Component({...})
 * export class LoginComponent {
 *   private readonly auth = inject(AuthService);
 * }
 *
 */
@Injectable({providedIn: 'any'})
export class AuthService {

  private userResourceService: UserResourceService = inject(UserResourceService)

  private redirectUrl: string | null

  private readonly authUrl: string = "/api/authenticate";
  private readonly signupUrl: string = '/api/account/register'

  private user: UserImpl = inject(UserImpl)

  constructor(private http: HttpClient) {
    this.redirectUrl = null
  }

  /**
   * Получить URL для перенаправления после входа.
   *
   * @returns URL перенаправления или `null`, если не задан.
   *
   * @example
   * ```ts
   * const redirect = this.auth.getRedirectUrl;
   *
   */
  get getRedirectUrl(): string | null {
    return this.redirectUrl
  }

  /**
   * Установить URL для перенаправления после входа.
   *
   * @param val URL перенаправления. `null` означает сброс значения.
   *
   * @example
   * ```ts
   * this.auth.setRedirectUrl = '/home';
   *
   */
  set setRedirectUrl(val: string | null) {
    this.redirectUrl = val
  }

  /**
   * Выполнить вход пользователя.
   *
   * @param data Данные для входа.
   * @returns Результат аутентификации.
   *
   * @example
   * ```ts
   * this.auth.login(payload).subscribe(auth => {
   *   this.token = auth.id_token;
   * });
   *
   */
  login(data: Login): Observable<AuthRequest> {
    return this.http.post<AuthRequest>(this.authUrl, data)
      .pipe(
        tap(value => {
          this.parseToken(value.id_token)
        })
      )
  }

  /**
   * Зарегистрировать пользователя.
   *
   * @param data Данные для регистрации.
   * @returns Результат регистрации.
   *
   * @example
   * ```ts
   * this.auth.registerAccountResource(payload).subscribe(() => {
   *   this.isRegistered = true;
   * });
   *
   */
  registerAccountResource(data: UserRegistration): Observable<any> {
    return this.http.post<any>(this.signupUrl, data)
  }

  /**
   * Разобрать токен и заполнить данные пользователя.
   *
   * @param token JWT-токен пользователя.
   * @returns Нет значения.
   *
   * @example
   * ```ts
   * this.auth.parseToken(token);
   *
   */
  parseToken(token: string): void {
    let decodeUser: decodeUser = JSON.parse(atob(token.split('.')[1]))
    let parsedAuth: Record<keyof typeof AuthoritiesConstants, (keyof typeof UPermission)[]> = JSON.parse(decodeUser.auth_json)

    this.onLogin(decodeUser, parsedAuth, token).then(() => {
      this.userResourceService.list({page: 0, size: 10, sort: 'id,asc'}, decodeUser.name)
        .pipe(
          map(value => {
            return value.content[0]
          })
        )
        .subscribe(res => {
          this.user.id = res.id
          this.user.email = res.email
          this.user.groups = res.currentGroups
        })
    })
  }

  /**
   * Сохранить данные пользователя после входа.
   *
   * @param data Декодированные данные пользователя.
   * @param parsedAuth Права доступа пользователя.
   * @param token JWT-токен пользователя.
   * @returns Нет значения.
   *
   * @example
   * ```ts
   * await this.auth.onLogin(decoded, permissions, token);
   *
   */
  async onLogin(data: decodeUser, parsedAuth: Record<keyof typeof AuthoritiesConstants, (keyof typeof UPermission)[]>, token: string): Promise<void> {
    this.user.onLogin({
      id: 0,
      login: data.name,
      userName: data.sub,
      token: token,
      expire: data.exp,
      permissions: parsedAuth
    })
  }
}
